import React, { useState, useEffect } from "react";
import axios from "axios";
import Gallery from "./Gallery";
import confetti from 'canvas-confetti';

function Dashboard() {
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [bucketName, setBucketName] = useState(() => {
    return sessionStorage.getItem("bucketName") || "";
  });
  const [showGallery, setShowGallery] = useState(false);
  const [buckets, setBuckets] = useState([]);
  const [selectedBucket, setSelectedBucket] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('darkMode') === 'true'
  });
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    fetchBuckets();
  }, []);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const handleFileSelect = (event) => {
    const newFiles = Array.from(event.target.files);
    if (newFiles.length > 0) {
      setSelectedFiles(prevFiles => [...prevFiles, ...newFiles]);
      setFiles([]); // Clear the uploaded files list
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    
    const newFiles = Array.from(event.dataTransfer.files);
    if (newFiles.length > 0) {
      setSelectedFiles(prevFiles => [...prevFiles, ...newFiles]);
      setFiles([]); // Clear the uploaded files list
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragging(false);
  };

  const fetchBuckets = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/buckets`
      );
      setBuckets(response.data.buckets);
    } catch (error) {
      console.error("Error fetching buckets:", error);
    }
  };

  const handleBucketSelection = (event) => {
    const newBucket = event.target.value;
    sessionStorage.setItem("bucketName", newBucket);
    setSelectedBucket(newBucket);
    setBucketName(newBucket);
    setFiles([]); // Clear the files list when bucket changes
  };

  const handleFileUpload = async () => {
    if (selectedFiles.length === 0) {
      alert("Please select files first");
      return;
    }

    setIsLoading(true);

    try {
      const uploadPromises = selectedFiles.map(async (file) => {
        const formData = new FormData();
        formData.append("file", file);

        if (bucketName) {
          formData.append("bucket", bucketName);
        }

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/upload`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              "X-API-Key": process.env.REACT_APP_API_KEY,
            },
          }
        );

        return {
          name: file.name,
          id: response.data.fileId,
          bucket: response.data.bucket,
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      setFiles(prevFiles => [...prevFiles, ...uploadedFiles]);
      setSelectedFiles([]); // Clear selected files after upload

      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    } catch (error) {
      console.error("Upload failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBucketNameChange = (event) => {
    setBucketName(event.target.value);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert("Copied to clipboard!");
    });
  };

  return (
    <div className="max-w-4xl mx-auto p-6 font-sans">
      <h1 className="text-3xl font-bold text-center text-gray-800 mb-6 dark:text-white">
        File CDN
      </h1>

      <nav className="flex justify-center space-x-4 mb-6">
        <button
          className={`px-4 py-2 rounded ${
            !showGallery
              ? "bg-blue-500 text-white dark:bg-gray-500 dark:text-gray-700"
              : "bg-gray-200 text-gray-700 dark:bg-gray-200 dark:text-gray-700"
          }`}
          onClick={() => setShowGallery(false)}
        >
          Upload
        </button>
        <button
          className={`px-4 py-2 rounded ${
            showGallery
              ? "bg-blue-500 text-white dark:bg-gray-500 dark:text-gray-700"
              : "bg-gray-200 text-gray-700 dark:bg-gray-200 dark:text-gray-700"
          }`}
          onClick={() => setShowGallery(true)}
        >
          Gallery
        </button>
        <button
          className={`px-4 py-2 rounded ${
            showGallery
              ? "bg-gray-500 text-white dark:bg-gray-500 dark:text-gray-700"
              : "bg-gray-200 text-gray-700 dark:bg-gray-200 dark:text-gray-700"
          }`}
          onClick={toggleDarkMode}
        >
          {darkMode ? 'Light Mode' : 'Dark Mode'}
        </button>
      </nav>

      {!showGallery ? (
        <div className="space-y-6">
          <div className="flex items-center space-x-4">
            <select
              className="relative m-0 block w-full min-w-0 flex-auto cursor-pointer rounded border border-solid border-secondary-500 bg-transparent bg-clip-padding px-3 py-[0.32rem] text-base font-normal text-surface transition duration-300 ease-in-out file:-mx-3 file:-my-[0.32rem] file:me-3 file:cursor-pointer file:overflow-hidden file:rounded-none file:border-0 file:border-e file:border-solid file:border-inherit file:bg-transparent file:px-3  file:py-[0.32rem] file:text-surface focus:border-primary focus:text-gray-700 focus:shadow-inset focus:outline-none dark:border-white/70 dark:text-white  file:dark:text-white"
              value={selectedBucket}
              onChange={handleBucketSelection}
            >
              <option value="">Select a bucket</option>
              {buckets.map((bucket) => (
                <option key={bucket} value={bucket}>
                  {bucket}
                </option>
              ))}
            </select>
            <span className="text-gray-500">or</span>
            <input
              type="text"
              className="relative m-0 block w-full min-w-0 flex-auto cursor-pointer rounded border border-solid border-secondary-500 bg-transparent bg-clip-padding px-3 py-[0.32rem] text-base font-normal text-surface transition duration-300 ease-in-out file:-mx-3 file:-my-[0.32rem] file:me-3 file:cursor-pointer file:overflow-hidden file:rounded-none file:border-0 file:border-e file:border-solid file:border-inherit file:bg-transparent file:px-3  file:py-[0.32rem] file:text-surface focus:border-primary focus:text-gray-700 focus:shadow-inset focus:outline-none dark:border-white/70 dark:text-white  file:dark:text-white"
              placeholder="Enter new bucket name"
              value={bucketName}
              onChange={handleBucketNameChange}
            />
          </div>

          <div
            className={`mt-4 p-6 border-2 border-dashed rounded-lg text-center ${
              isDragging
                ? "border-blue-500 bg-blue-50 dark:border-gray-500 dark:bg-gray-800"
                : "border-gray-300 dark:border-gray-600"
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <input
              type="file"
              onChange={handleFileSelect}
              className="hidden"
              id="fileInput"
              multiple
            />
            <label
              htmlFor="fileInput"
              className="cursor-pointer text-blue-500 dark:text-gray-300"
            >
              Click to select files or drag and drop them here
            </label>
            {selectedFiles.length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Selected files ({selectedFiles.length}):
                </p>
                <ul className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {selectedFiles.map((file, index) => (
                    <li key={index}>{file.name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {selectedFiles.length > 0 && (
            <>
              <button
                onClick={handleFileUpload}
                disabled={isLoading}
                className={`px-4 py-2 rounded ${
                  isLoading
                    ? "bg-gray-400"
                    : "bg-blue-500 hover:bg-blue-600 dark:bg-gray-500 dark:hover:bg-gray-600"
                } text-white transition`}
              >
                {isLoading ? "Uploading..." : "Upload"}
              </button>
            </>
          )}

          <ul className="space-y-2">
            {files.map((file) => (
              <li
                key={file.id}
                className="flex items-center justify-between bg-gray-100 p-3 rounded"
              >
                <span className="truncate flex-1">
                  {file.name}
                </span>
                <button
                  onClick={() =>
                    copyToClipboard(
                      `${process.env.REACT_APP_API_URL}/asset/${file.id}`
                    )
                  }
                  className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                >
                  Copy Link
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <Gallery />
      )}
    </div>
  );
}

export default Dashboard;
